import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { siteDiaryAPI, jobsAPI } from '../utils/api';
import { toast } from 'sonner';
import {
  ClipboardList, Plus, X, Loader2, Calendar, Building2, ChevronRight,
  BarChart3, Users, CloudRain, TrendingDown, Search,
} from 'lucide-react';

export const STATUS_META = {
  draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  submitted: { label: 'Under Review', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  signed_off: { label: 'Approved & Locked', cls: 'bg-green-100 text-green-800 border-green-200' },
  void: { label: 'Voided', cls: 'bg-red-100 text-red-700 border-red-200' },
};

const StatusBadge = ({ status }) => {
  const m = STATUS_META[status] || STATUS_META.draft;
  return <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full border ${m.cls}`}>{m.label}</span>;
};

const KpiCard = ({ icon: Icon, label, value, sub, color = 'teal' }) => (
  <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
    <div className="flex items-center gap-2 text-slate-500 text-sm">
      <Icon className={`w-4 h-4 text-${color}-600`} />{label}
    </div>
    <div className="text-2xl font-bold text-slate-800 mt-1">{value}</div>
    {sub && <div className="text-xs text-slate-400 mt-0.5">{sub}</div>}
  </div>
);

export default function SiteDiary() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('diaries');
  const [diaries, setDiaries] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ project_id: '', diary_date: new Date().toISOString().split('T')[0] });
  const [creating, setCreating] = useState(false);

  // analytics
  const [util, setUtil] = useState(null);
  const [attr, setAttr] = useState(null);
  const [weather, setWeather] = useState(null);
  const [burnRate, setBurnRate] = useState(1500);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const [d, j] = await Promise.all([siteDiaryAPI.list(params), jobsAPI.getAll()]);
      setDiaries(d.data || []);
      setJobs(j.data || []);
    } catch (e) {
      toast.error('Failed to load site diaries');
    } finally { setLoading(false); }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const loadAnalytics = useCallback(async () => {
    try {
      const [u, a, w] = await Promise.all([
        siteDiaryAPI.laborUtilization(),
        siteDiaryAPI.attrition(),
        siteDiaryAPI.weatherDowntime({ burn_rate_per_hour: burnRate }),
      ]);
      setUtil(u.data); setAttr(a.data); setWeather(w.data);
    } catch (e) { toast.error('Failed to load analytics'); }
  }, [burnRate]);

  useEffect(() => { if (tab === 'analytics') loadAnalytics(); }, [tab, loadAnalytics]);

  const handleCreate = async () => {
    if (!form.project_id || !form.diary_date) { toast.error('Pick a project and date'); return; }
    setCreating(true);
    try {
      const res = await siteDiaryAPI.create(form);
      toast.success('Diary created');
      setShowCreate(false);
      navigate(`/site-diary/${res.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not create diary');
    } finally { setCreating(false); }
  };

  const filtered = diaries.filter((d) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (d.project_name || '').toLowerCase().includes(s) ||
      (d.project_code || '').toLowerCase().includes(s) ||
      (d.diary_date || '').includes(s);
  });

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-5">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-teal-600" /> Site Diary
          </h1>
          <p className="text-sm text-slate-500">Daily record of labour, plant, deliveries, progress & safety.</p>
        </div>
        <button onClick={() => setShowCreate(true)} data-testid="new-diary-btn"
          className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg font-medium shadow-sm">
          <Plus className="w-4 h-4" /> New Diary
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 mb-4">
        {[['diaries', 'Diaries', ClipboardList], ['analytics', 'Analytics', BarChart3]].map(([k, lbl, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-teal-600 text-teal-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            <Icon className="w-4 h-4" />{lbl}
          </button>
        ))}
      </div>

      {tab === 'diaries' && (
        <>
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search project or date..."
                className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none">
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="submitted">Under Review</option>
              <option value="signed_off">Approved & Locked</option>
              <option value="void">Voided</option>
            </select>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <ClipboardList className="w-12 h-12 mx-auto mb-3 opacity-40" />
              No diaries yet. Create your first daily entry.
            </div>
          ) : (
            <div className="grid gap-3">
              {filtered.map((d) => (
                <button key={d.id} onClick={() => navigate(`/site-diary/${d.id}`)} data-testid="diary-card"
                  className="w-full text-left bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md hover:border-teal-300 transition p-4 flex items-center justify-between">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-slate-800 truncate">{d.project_name || 'Project'}</span>
                      {d.project_code && <span className="text-xs text-slate-400">{d.project_code}</span>}
                      <StatusBadge status={d.status} />
                      {d.version > 1 && <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200">v{d.version}</span>}
                    </div>
                    <div className="flex items-center gap-4 mt-1.5 text-sm text-slate-500">
                      <span className="inline-flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{d.diary_date}</span>
                      <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5" />{d.labor_count || 0} labour</span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-300 shrink-0" />
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {tab === 'analytics' && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <KpiCard icon={BarChart3} label="Labour Utilisation" value={util ? `${util.utilization_pct}%` : '—'}
              sub={util ? `${util.total_actual_man_hours}h actual / ${util.total_planned_man_hours}h planned` : ''} />
            <KpiCard icon={TrendingDown} label="Avg Daily Attrition" value={attr ? `${attr.avg_daily_attrition_pct}%` : '—'}
              sub={attr ? `Peak head-count ${attr.peak_head_count}` : ''} color="orange" />
            <KpiCard icon={CloudRain} label="Weather Downtime Cost" value={weather ? `R ${weather.total_weather_downtime_cost.toLocaleString()}` : '—'}
              sub={weather ? `${weather.total_weather_delay_hours}h across ${weather.weather_delay_events} events` : ''} color="blue" />
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center gap-3 mb-3">
              <label className="text-sm text-slate-600">Site burn rate (ZAR / hour):</label>
              <input type="number" value={burnRate} onChange={(e) => setBurnRate(Number(e.target.value))}
                className="w-32 px-3 py-1.5 border border-slate-300 rounded-lg text-sm" data-testid="burn-rate-input" />
              <button onClick={loadAnalytics} className="text-sm bg-slate-800 text-white px-3 py-1.5 rounded-lg">Recalculate</button>
            </div>
            <p className="text-xs text-slate-400">Weather Downtime Cost = weather-delay hours × burn rate.</p>
          </div>

          {util && util.by_trade?.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
              <h3 className="font-semibold text-slate-800 mb-3">Planned vs Actual Man-hours by Trade</h3>
              <div className="space-y-2">
                {util.by_trade.map((t) => {
                  const pct = t.planned ? Math.min(100, (t.actual / t.planned) * 100) : 0;
                  return (
                    <div key={t.trade}>
                      <div className="flex justify-between text-sm text-slate-600 mb-0.5">
                        <span className="capitalize">{t.trade.replace(/_/g, ' ')}</span>
                        <span>{t.actual}h / {t.planned}h</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-teal-500 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
              <h3 className="font-semibold text-slate-800">New Site Diary</h3>
              <button onClick={() => setShowCreate(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-600 flex items-center gap-1"><Building2 className="w-4 h-4" /> Project</label>
                <select value={form.project_id} onChange={(e) => setForm((p) => ({ ...p, project_id: e.target.value }))}
                  data-testid="diary-project-select"
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none">
                  <option value="">Select a project...</option>
                  {jobs.map((j) => <option key={j.id} value={j.id}>{j.job_code ? `${j.job_code} — ` : ''}{j.title || j.client_name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 flex items-center gap-1"><Calendar className="w-4 h-4" /> Diary date</label>
                <input type="date" value={form.diary_date} onChange={(e) => setForm((p) => ({ ...p, diary_date: e.target.value }))}
                  data-testid="diary-date-input"
                  className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 outline-none" />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-100 flex justify-end gap-2">
              <button onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
              <button onClick={handleCreate} disabled={creating} data-testid="create-diary-submit"
                className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-60">
                {creating && <Loader2 className="w-4 h-4 animate-spin" />} Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
